<template>
  <div>
    <div v-if="voucherSendSuccess">
      {{ $t('payment.payment_confirmed.subtitle') }}
    </div>
    <div v-else>
      <div class="mb-6 rounded-lg border py-6 text-center text-h1">
        <template v-if="pricePartlyCovered">
          {{
            $t('payment.priceWithPartnerDiscount', {
              price: questionPrice,
              discountPercent: currentMutation.discountValue,
              partnerName: partnerData.name
            })
          }}
        </template>
        <template v-else>
          {{ $t('payment.priceSum', { price: questionPrice }) }}
        </template>
      </div>

      <div class="group-list">
        <div
          v-for="(group, groupIndex) in availablePaymentMethodGroups"
          :key="groupIndex"
          class="group-list--item"
          :class="{
            hidden: selectedGroupTitle && selectedGroupTitle !== group.title,
            'mt-6': groupIndex > 0 && !(selectedGroupTitle && selectedGroupTitle !== group.title)
          }"
        >
          <div class="text-h2 mb-4">{{ getMethodGroupTitle(group) }}</div>

          <div class="methods-list">
            <div :data-group-index="groupIndex" class="methods-list--before-selected"></div>
            <div :data-group-index="groupIndex" class="methods-list--always-shown">
              <PaymentMethod
                v-for="(method, methodIndex) in group.methods.filter(mtd => mtd.name !== 'voucher')"
                :key="methodIndex"
                :method="method"
                :selected="selectedMethod && selectedMethod.id === method.id"
                @click="selectedMethod ? showMore(groupIndex) : selectMethod($event)"
              />
              <v-row v-if="group.methods.filter(mtd => mtd.name === 'voucher').length" align="end">
                <v-col cols="12" sm="8">
                  <label for="voucher">{{ $t('payment.voucher') }}</label>
                  <v-text-field
                    id="voucher"
                    v-model="$v.voucherCode.$model"
                    data-cy="goPayVoucherTextField"
                    solo
                    flat
                    placeholder="A1B2C3"
                    :class="[{ 'is-valid': $v.voucherCode.$dirty && !$v.voucherCode.$invalid }]"
                    :error="voucherCodeError || $v.voucherCode.$error"
                    :error-messages="voucherErrorMessage"
                    :hide-details="!voucherCodeError"
                    :disabled="voucherSendLoading"
                    @keydown="
                      voucherCodeError = false;
                      voucherErrorMessage = null;
                    "
                  >
                    <template #append>
                      <v-icon v-if="$v.voucherCode.$dirty && !$v.voucherCode.$invalid">
                        $tick
                      </v-icon>
                      <v-icon v-if="$v.voucherCode.$error">$warning</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="4" :class="{ 'align-self-center': voucherCodeError }">
                  <SendButton
                    block
                    data-cy="goPaySendVoucherButton"
                    :disabled="!$v.voucherCode.$dirty || $v.voucherCode.$error"
                    :loading="voucherSendLoading"
                    :button-text="$t('payment.voucherApply')"
                    @click="getPaymentUrl(group.methods.find(mtd => mtd.name === 'voucher'))"
                  />
                </v-col>
              </v-row>
            </div>
            <div :data-group-index="groupIndex" class="methods-list--after-selected hidden"></div>
            <v-btn
              :data-group-index="groupIndex"
              class="show-more-button hidden"
              block
              color="tetrary"
              @click="showMore(groupIndex)"
            >
              <v-icon class="mr-2">$dropSmall</v-icon>
              {{ selectedGroupTitle ? $t('payment.chooseDifferent') : $t('showMore') }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { minLength, required } from 'vuelidate/lib/validators';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import goPay from '@/mixins/goPay';
import PaymentMethod from '@/components/Atoms/PaymentMethod';
import SendButton from '@/components/Forms/Parts/SendButton';
import ecommerceGtm from '@/mixins/ecommerceGtm';
import partnerDataCurrentMutation from '@/mixins/currentMutation/partnerDataCurrentMutation';
import { getQuestionDefaultPrice } from '@/feature/questions/helpers/questionPricingHelpers';

export default {
  name: 'GoPay',
  components: { SendButton, PaymentMethod },
  mixins: [goPay, ecommerceGtm, partnerDataCurrentMutation],
  data() {
    return {
      voucherCode: null,
      voucherCodeError: false,
      voucherErrorMessage: null,
      voucherSendLoading: false,
      voucherSendSuccess: false,
      expandedGroups: [],
      selectedGroupTitle: null,
      selectedMethod: null,
      paymentLoading: false
    };
  },
  computed: {
    applePayAvailable() {
      return window.ApplePaySession?.canMakePayments();
    },
    availablePaymentMethodGroups() {
      return this.backendPaymentMethodGroups.map(group => {
        return {
          title: group.title,
          methods: group.methods.filter(mtd =>
            this.applePayAvailable ? true : mtd.name !== 'onlineIos'
          )
        };
      });
    },
    questionPrice() {
      return this.$auth.user.benefit?.active && this.currentMutation.questionPrice
        ? this.currentMutation.questionPrice
        : getQuestionDefaultPrice(this.$domainRegion);
    },
    ...mapGetters({
      dialogLoading: 'profile/goPay/dialogLoading'
    })
  },
  watch: {
    voucherSendLoading() {
      this.setDialogData();
    },
    selectedMethod() {
      this.setDialogData();
    },
    paymentLoading() {
      this.setDialogData();
    },
    voucherSendSuccess(newValue) {
      this.setDialogData();
      if (newValue) {
        this.sendQuestionGtm('voucher', this.identifier, this.questionPrice);
      }
    },
    dialogLoading(newValue) {
      this.setDialogData();
      if (!newValue) {
        this.$nextTick(() => {
          this.availablePaymentMethodGroups.forEach((group, groupIndex) => {
            if (group.methods.length <= 3) {
              return;
            }

            this.$el
              .querySelector(`.show-more-button[data-group-index="${groupIndex}"]`)
              .classList.remove('hidden');
            const afterSelectedList = this.$el.querySelector(
              `.methods-list--after-selected[data-group-index="${groupIndex}"]`
            );
            for (let iter = 3; iter < group.methods.length; iter++) {
              afterSelectedList.appendChild(
                this.$el.querySelector(
                  `.methods-list .method[data-title="${group.methods[iter].name}"]`
                )
              );
            }
          });
        });
      }
    }
  },
  validations() {
    return {
      voucherCode: {
        required,
        minLength: minLength(2)
      }
    };
  },
  mounted() {
    this.setDialogData();
  },
  methods: {
    ...mapMutations({
      setPaymentMethodName: 'profile/dashboard/setPaymentMethodName'
    }),
    ...mapActions({
      setQuestions: 'profile/dashboard/setQuestions',
      setDialogTitle: 'profile/goPay/setDialogTitle',
      setDialogButtons: 'profile/goPay/setDialogButtons',
      setDialogLeftHeaderIcon: 'profile/goPay/setDialogLeftHeaderIcon',
      setDialogLoading: 'profile/goPay/setDialogLoading',
      setDialogHeaderType: 'profile/goPay/setDialogHeaderType'
    }),
    startNow() {
      this.$emit('close');
    },

    getPaymentUrl(method) {
      this.gtmPush({ event: 'checkout_option' });
      const payload = {
        paymentMethodId: method.id,
        paymentProviderId: method.paymentProviderId,
        regionCode: this.$domainRegion
      };
      const useVoucher = method.name === 'voucher';
      if (useVoucher) {
        payload.voucherCode = this.voucherCode;
        this.voucherSendLoading = true;
      } else {
        this.paymentLoading = true;
      }

      this.setPaymentMethodName(method.name);
      this.$http
        .post(`/v1/questions/${this.identifier}/paymentMethod`, payload)
        .then(data => {
          if (useVoucher) {
            if (data.status && data.status.id) {
              if (data.status.id === 3) {
                this.setQuestions();
                this.voucherSendSuccess = true;
              } else if (data.status.id === 16) {
                this.voucherCodeError = true;
                this.voucherErrorMessage = this.$t('authorization.codeHrbError');
              }
            }
          } else if (data.token && data.token.length) {
            this.gtmPush({ event: 'checkout' });
            window.location = data.token;
          }
        })
        .catch(err => {
          this.setDialogLoading(false);
          this.handleError({ error: err, code: '0x0000001F' });
        })
        .finally(() => {
          this.voucherSendLoading = false;
        });
    },
    getMethodGroupTitle(item) {
      if (
        item?.methods?.find(val => {
          return val.name === 'voucher';
        })
      ) {
        return this.$t('payment.voucherGroup', {
          region: this.$domainRegion === 'cz' ? 'uLékaře.cz' : 'uLékara.sk'
        });
      } else {
        return item?.title;
      }
    },
    showMore(groupIndex) {
      let beforeSelectedLists;
      let afterSelectedLists;
      let showMoreButtons;

      if (this.selectedGroupTitle) {
        this.selectedMethod = null;
        this.expandedGroups = this.availablePaymentMethodGroups.map(grp => grp.title);
        beforeSelectedLists = this.$el.querySelectorAll('.methods-list--before-selected');
        afterSelectedLists = this.$el.querySelectorAll('.methods-list--after-selected');
        showMoreButtons = this.$el.querySelectorAll('.show-more-button');
      } else {
        beforeSelectedLists = this.$el.querySelectorAll(
          `.methods-list--before-selected[data-group-index="${groupIndex}"]`
        );
        afterSelectedLists = this.$el.querySelectorAll(
          `.methods-list--after-selected[data-group-index="${groupIndex}"]`
        );
        showMoreButtons = this.$el.querySelectorAll(
          `.show-more-button[data-group-index="${groupIndex}"]`
        );
        this.expandedGroups.push(this.availablePaymentMethodGroups[groupIndex].title);
      }

      showMoreButtons.forEach(el => el.classList.add('hidden'));
      window.setTimeout(() => {
        beforeSelectedLists.forEach(el => el.classList.remove('hidden'));
        afterSelectedLists.forEach(el => el.classList.remove('hidden'));
        if (!this.selectedMethod) {
          this.selectedGroupTitle = null;
        }
      }, 250);
    },
    selectMethod(method) {
      const selectedGroupIndex = this.availablePaymentMethodGroups.findIndex(
        group => group.methods.findIndex(mtd => mtd.name === method.name) > -1
      );
      const selectedMethodIndex = this.availablePaymentMethodGroups[
        selectedGroupIndex
      ].methods.findIndex(mtd => mtd.name === method.name);

      const beforeSelectedList = this.$el.querySelector(
        `.methods-list--before-selected[data-group-index="${selectedGroupIndex}"]`
      );
      const alwaysShowList = this.$el.querySelector(
        `.methods-list--always-shown[data-group-index="${selectedGroupIndex}"]`
      );
      const afterSelectedList = this.$el.querySelector(
        `.methods-list--after-selected[data-group-index="${selectedGroupIndex}"]`
      );

      this.availablePaymentMethodGroups[selectedGroupIndex].methods.forEach((mtd, mtdIndex) => {
        const mtdElement = this.$el.querySelector(`.methods-list [data-title="${mtd.name}"]`);
        if (mtdIndex < selectedMethodIndex) {
          beforeSelectedList.appendChild(mtdElement);
        }

        if (mtdIndex === selectedMethodIndex) {
          alwaysShowList.appendChild(mtdElement);
        }

        if (mtdIndex > selectedMethodIndex) {
          afterSelectedList.appendChild(mtdElement);
        }
      });

      this.selectedMethod = method;
      this.selectedGroupTitle = this.availablePaymentMethodGroups[selectedGroupIndex].title;

      beforeSelectedList.classList.add('hidden');
      afterSelectedList.classList.add('hidden');

      window.setTimeout(() => {
        this.$el
          .querySelector(`.show-more-button[data-group-index="${selectedGroupIndex}"]`)
          .classList.remove('hidden');
      }, 250);
    },
    setDialogData() {
      this.setDialogTitle(
        this.voucherSendSuccess
          ? this.$t('payment.payment_confirmed.title')
          : this.$t('payment.title')
      );
      this.setDialogLeftHeaderIcon(this.voucherSendSuccess ? 'icons/confetti.svg' : '');
      this.setDialogHeaderType(this.voucherSendSuccess ? 'information' : 'navigation');
      let buttons = [];
      if (!this.voucherSendSuccess) {
        buttons.push({
          type: 'sendButton',
          props: {
            disabled: !this.selectedMethod,
            loading: this.paymentLoading,
            dataCy: 'goPaySendButtonPay'
          },
          events: {
            click: () => {
              this.getPaymentUrl(this.selectedMethod);
            }
          },
          text: this.$t('payment.pay', { price: this.questionPrice })
        });
      }

      if (!this.voucherSendLoading && this.voucherSendSuccess) {
        buttons.push({
          events: {
            click: () => {
              this.startNow();
            }
          },
          text: this.$t('finish')
        });
      }
      this.setDialogButtons(buttons);
    }
  }
};
</script>

<style lang="scss" scoped>
.show-more-button {
  max-height: 38px;
}

.group-list--item {
  max-height: 1000px;
}

.methods-list {
  &--before-selected,
  &--after-selected {
    max-height: 500px;
  }
}

.group-list--item,
.methods-list--before-selected,
.methods-list--after-selected,
.show-more-button {
  transition: max-height 0.5s ease-in-out;
  overflow: hidden;

  &.hidden {
    max-height: 0;
  }
}
</style>
